import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth, signInWithEmailAndPassword } from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from './AuthContext';
import './LoginView.css';
import { Helmet } from 'react-helmet';
import landingConfig from '../config/landing.config';

const LoginView = () => {
  const {
    logo = '/logo-prwd.svg',
    siteName = 'Puerto Rico Windows & Doors',
    theme = {},
  } = landingConfig;

  const primary = theme.primary || '#0f5132';
  const primaryDark = theme.primaryDark || '#0b3d24';
  const cardBorder = theme.cardBorder || '#d3e1d1';
  const heroImage =
    'https://images.unsplash.com/photo-1484154218962-a197022b5858?auto=format&fit=crop&w=1650&q=80';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { setUserRole } = useAuth();

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    const auth = getAuth();

    console.log('[handleLogin] 🔐 Iniciando sesión...');
    console.log(`[handleLogin] email: ${email}`);

    try {
      const { user } = await signInWithEmailAndPassword(auth, email, password);
      const uid = user.uid;
      console.log(`[handleLogin] ✅ UID: ${uid}`);

      const userRef = doc(db, 'users', uid);
      let userSnap = await getDoc(userRef);
      const tokenResult = await user.getIdTokenResult();
      const claimedRole = tokenResult?.claims?.role;

      // 🔄 Si no existe el doc, lo crea automáticamente
      if (!userSnap.exists()) {
        console.warn(`[handleLogin] ⚠️ No existe documento Firestore para UID: ${uid}`);
        await setDoc(userRef, {
          uid,
          email,
          role: claimedRole || 'client',
          createdAt: serverTimestamp()
        });
        console.log('[handleLogin] 🆕 Documento Firestore creado para UID:', uid);
        userSnap = await getDoc(userRef); // reintenta
      }

      let userData = userSnap.data();
      let effectiveRole = userData.role || claimedRole || null;

      if (claimedRole && userData.role !== claimedRole) {
        try {
          await updateDoc(userRef, { role: claimedRole, updatedAt: serverTimestamp() });
          userData = { ...userData, role: claimedRole };
          effectiveRole = claimedRole;
          console.log(`[handleLogin] 🔄 Rol actualizado a ${claimedRole} por custom claim.`);
        } catch (syncErr) {
          console.warn('No pudimos sincronizar el rol con Firestore:', syncErr);
        }
      }

      console.log('[handleLogin] 📄 Datos Firestore:', userData);

      if (!effectiveRole) {
        setError('Tu cuenta no tiene rol asignado. Contacta al administrador.');
        return;
      }

      setUserRole(effectiveRole);
      console.log(`[handleLogin] 🚀 Rol: ${effectiveRole}`);

      if (effectiveRole === 'admin') {
        navigate('/admin/clients');
      } else if (effectiveRole === 'client') {
        navigate('/clients/dashboard');
      } else if (effectiveRole === 'seller') {
        navigate('/seller');
      } else if (effectiveRole === 'warehouse') {
        navigate('/warehouse');
      } else if (effectiveRole === 'technician' || effectiveRole === 'delivery') {
        navigate('/technician');
      } else {
        setError('Rol de usuario no reconocido.');
      }

    } catch (err) {
      console.error('[handleLogin] ❌ Error Firebase:', err.code, err.message);
      if (err.code === 'auth/user-not-found') {
        setError('Usuario no encontrado.');
      } else if (err.code === 'auth/wrong-password') {
        setError('Contraseña incorrecta.');
      } else if (err.code === 'auth/too-many-requests') {
        setError('Demasiados intentos. Intenta más tarde.');
      } else {
        setError('Credenciales inválidas o error inesperado.');
      }
    }
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center bg-gray-900 px-4">
      <Helmet>
        <title>Acceso seguro | Puerto Rico Windows & Doors</title>
        <meta
          name="description"
          content="Ingresa como cliente, administrador o vendedor de Puerto Rico Windows & Doors y gestiona tus proyectos."
        />
      </Helmet>
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: `linear-gradient(135deg, ${primary}cc, ${primaryDark}cc)`,
        }}
      ></div>
      <div className="absolute inset-0">
        <img
          src={heroImage}
          alt="Arquitectura premium"
          className="w-full h-full object-cover opacity-40"
        />
      </div>

      <div className="relative z-10 w-full max-w-xl bg-white/95 backdrop-blur rounded-3xl shadow-2xl border p-8 space-y-6" style={{ borderColor: cardBorder }}>
        <div className="flex flex-col items-center text-center">
          <img src={logo} alt={siteName} className="h-24 max-w-[260px] w-auto mb-3 object-contain" />
          <h1 className="text-xl font-semibold text-gray-900">{siteName}</h1>
          <p className="text-sm text-gray-500">
            Ingresa con tus credenciales. Detectamos automáticamente si tu perfil es cliente, administrador o vendedor.
          </p>
        </div>

        <form onSubmit={handleLogin} className="space-y-4">
          <div>
            <label className="text-sm font-semibold text-gray-700">Correo electrónico</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="mt-1 w-full px-4 py-2.5 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>
          <div>
            <label className="text-sm font-semibold text-gray-700">Contraseña</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="mt-1 w-full px-4 py-2.5 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>
          <button
            type="submit"
            className="w-full py-3 rounded-2xl text-white font-semibold shadow-lg transition"
            style={{ backgroundImage: `linear-gradient(120deg, ${primary}, ${primaryDark})` }}
          >
            Ingresar
          </button>
          {error && <p className="text-sm text-red-500 text-center">{error}</p>}
        </form>
      </div>
    </div>
  );
};

export default LoginView;
